import React from "react";
import { accord } from "../../../lib/accord-nombres";
import {
  COULEUR,
  LARGEUR,
  Figure,
  PhraseAbsence,
  libelleMoment,
} from "./_commun";

// Jours — une grille de jours : arrivées, départs et ménages mêlés.
//
// Ce qu'elle apprend (registre) : « arrivées, départs et ménages sur une même
// grille de jours ». Le planning est un mois ; celle-ci tient une semaine
// entière dans un seul regard.
//
// ⚠️ LA PLUS FRAGILE DES SIX (registre). Si elle finit par ressembler au
// planning, elle meurt. Donc : une colonne par jour, TROIS lignes, et rien
// d'autre — pas de nom de voyageur, pas d'heure, pas de statut.
//
// ══ FORME DES DONNÉES ═══════════════════════════════════════════════════════
//
//   {
//     jours: [                                  // 1 à 14 jours, dans l'ordre
//       { date: "2026-09-08",                   // AAAA-MM-JJ, valide
//         arrivees: ["lc2"],                    // identifiants de logement (chaînes courtes)
//         departs:  [],
//         menages:  ["lc2"] },
//       …
//     ]
//   }
//
// Une semaine sans rien N'EST PAS dessinée : une grille vide ressemble à un
// planning vide, et la phrase « aucune arrivée, aucun départ, aucun ménage »
// dit la même chose sans se faire passer pour une mesure.

const JOURS_SEMAINE = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"];
const LIGNES = [
  {
    cle: "arrivees",
    label: "Arrivées",
    couleur: COULEUR.ciel,
    texte: COULEUR.navyD,
    un: "arrivée",
    plusieurs: "arrivées",
    feminin: true,
  },
  {
    cle: "departs",
    label: "Départs",
    couleur: COULEUR.navy,
    texte: COULEUR.fond,
    un: "départ",
    plusieurs: "départs",
    feminin: false,
  },
  {
    cle: "menages",
    label: "Ménages",
    couleur: COULEUR.ambre,
    texte: COULEUR.navyD,
    un: "ménage",
    plusieurs: "ménages",
    feminin: false,
  },
];
const MAX_JOURS = 14;
// ⚠️ AÉRÉE LE 2026-09-15. Serrée (26/30, textes à 10,5), la grille se lisait
// comme un bloc de texte collé sous la prose sur un écran de 375 px — c'est
// ce que Dimitri a rapporté. On garde exactement la même information : on lui
// donne de la place. Les tailles sont en unités de `viewBox` (largeur 400),
// donc les augmenter les grandit RÉELLEMENT à l'écran.
const MARGE = { gauche: 62, droite: 8, haut: 34, bas: 10 };
const HAUTEUR_CELLULE = 36;

function dateValide(s) {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

function listeIds(x) {
  if (x == null) return [];
  if (!Array.isArray(x)) return null;
  return x.every(
    (id) => typeof id === "string" && id.length > 0 && id.length <= 12,
  )
    ? x
    : null;
}

function validerJours(d) {
  if (
    !d ||
    typeof d !== "object" ||
    !Array.isArray(d.jours) ||
    d.jours.length === 0
  )
    return { erreur: "pas de jours à montrer" };
  if (d.jours.length > MAX_JOURS)
    return {
      erreur: `${d.jours.length} jours — au-delà de deux semaines, c'est le planning qu'il faut ouvrir`,
    };
  const jours = [];
  for (const j of d.jours) {
    if (!j || typeof j !== "object" || !dateValide(j.date))
      return { erreur: "données incomplètes — un jour n'a pas de date valide" };
    const arrivees = listeIds(j.arrivees);
    const departs = listeIds(j.departs);
    const menages = listeIds(j.menages);
    if (!arrivees || !departs || !menages)
      return {
        erreur: "données incomplètes — une liste du jour est malformée",
      };
    jours.push({ date: j.date, arrivees, departs, menages });
  }
  return { jours };
}

// L'accord passe par le helper commun (`src/lib/accord-nombres.js`) ; seul le
// « aucun/aucune » du zéro reste ici, parce qu'il est propre à ce dessin.
function pluriel(n, l) {
  return accord(n, l.un, {
    pluriel: l.plusieurs,
    zero: `${l.feminin ? "aucune" : "aucun"} ${l.un}`,
  });
}

// ── TOUT CE QUI EST AFFICHÉ MÈNE QUELQUE PART ──────────────────────────────
// Règle Dimitri, 2026-09-09 : « chaque code de logement mène à sa fiche, chaque
// jour à ma journée. Un dessin qui ne mène nulle part est une capture
// d'écran. »
//
// ⚠️ LE DESSIN NE NAVIGUE PAS LUI-MÊME. Il APPELLE `onAller` — c'est l'écran
// qui décide où ça mène. Un dessin qui connaîtrait les onglets de
// l'application serait un dessin qu'on ne peut plus réutiliser ailleurs, et
// qui casse le jour où un onglet est renommé.
// ⚠️ `noms` — LA GRILLE NON PLUS NE PARLE PAS EN CODES (Dimitri, 2026-09-09).
// Les cellules portaient « lc1 », « lc3 ». On affiche le NOM ; le code reste
// dans la donnée, parce que c'est lui qui ouvre la bonne fiche au clic.
export default function Jours({ titre, donnees, onAller, noms }) {
  const nommer = (id) => (noms && noms[id]) || id;
  // Un dessin SANS destination reste un dessin : on ne rend pas les cellules
  // cliquables si personne n'écoute. Un curseur qui promet un clic sans rien
  // faire est pire qu'un curseur inerte.
  const cliquable = typeof onAller === "function";
  const v = validerJours(donnees);
  if (v.erreur) return <PhraseAbsence titre={titre} texte={v.erreur} />;
  const { jours } = v;

  const totaux = LIGNES.map((l) =>
    jours.reduce((s, j) => s + j[l.cle].length, 0),
  );
  const periode = `du ${libelleMoment(jours[0].date)} au ${libelleMoment(jours[jours.length - 1].date)}`;
  const bilan = LIGNES.map((l, i) => pluriel(totaux[i], l)).join(", ");

  if (totaux.every((t) => t === 0))
    return <PhraseAbsence titre={titre} texte={`${periode} : ${bilan}`} />;

  const joursSansRien = jours.filter((j) =>
    LIGNES.every((l) => j[l.cle].length === 0),
  );
  const suffixe =
    joursSansRien.length > 0 && joursSansRien.length < jours.length
      ? ` ; rien ${joursSansRien.map((j) => JOURS_SEMAINE[new Date(`${j.date}T00:00:00Z`).getUTCDay()]).join(", ")}`
      : "";
  const conclusion = `${titre}, ${periode} : ${bilan}${suffixe}`;

  const n = jours.length;
  const largeurCol = (LARGEUR - MARGE.gauche - MARGE.droite) / n;
  const H = MARGE.haut + HAUTEUR_CELLULE * LIGNES.length + MARGE.bas;

  return (
    <Figure forme="jours" titre={titre} conclusion={conclusion} hauteur={H}>
      {/* En-tête : le jour de la semaine et le quantième. */}
      {jours.map((j, i) => {
        const d = new Date(`${j.date}T00:00:00Z`);
        const xc = MARGE.gauche + largeurCol * (i + 0.5);
        return (
          <text
            key={j.date}
            x={xc}
            y={MARGE.haut - 13}
            textAnchor="middle"
            fontSize="12"
            fill={COULEUR.navyD}
            fontWeight="600"
          >
            {`${JOURS_SEMAINE[d.getUTCDay()]} ${d.getUTCDate()}`}
          </text>
        );
      })}

      {/* Les jours : toute la colonne est une cible, pas seulement le libellé —
          viser huit pixels de texte au doigt est une promesse qu'on ne tient
          pas. */}
      {cliquable &&
        jours.map((j, i) => (
          <rect
            key={`jour-${j.date}`}
            data-testid={`jours-colonne-${j.date}`}
            x={MARGE.gauche + largeurCol * i}
            y={0}
            width={largeurCol}
            height={H}
            fill="transparent"
            style={{ cursor: "pointer" }}
            role="button"
            tabIndex={0}
            aria-label={`Ouvrir la journée du ${libelleMoment(j.date)}`}
            onClick={() => onAller({ quoi: "jour", date: j.date })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onAller({ quoi: "jour", date: j.date });
              }
            }}
          />
        ))}

      {LIGNES.map((l, r) => {
        const y0 = MARGE.haut + HAUTEUR_CELLULE * r;
        return (
          <g key={l.cle} data-testid={`jours-ligne-${l.cle}`}>
            <text
              x={MARGE.gauche - 8}
              y={y0 + HAUTEUR_CELLULE / 2 + 4}
              textAnchor="end"
              fontSize="12"
              fill={COULEUR.navyD}
            >
              {l.label}
            </text>
            <line
              x1={MARGE.gauche}
              x2={LARGEUR - MARGE.droite}
              y1={y0}
              y2={y0}
              stroke={COULEUR.bord}
            />
            {jours.map((j, i) => {
              const ids = j[l.cle];
              if (ids.length === 0) return null;
              const x0 = MARGE.gauche + largeurCol * i + 2;
              const w = largeurCol - 4;
              // Un nom complet ne tient pas dans une case de 60 px : on
              // abrège proprement plutôt que de déborder. Le titre complet
              // reste lisible au survol et pour un lecteur d'écran.
              const court = (id) => {
                const nom = nommer(id);
                return nom.length > 13 ? `${nom.slice(0, 12)}…` : nom;
              };
              const libelle =
                n > 7
                  ? String(ids.length)
                  : ids.length === 1
                    ? court(ids[0])
                    : `${ids.length} · ${court(ids[0])}`;
              // ⚠️ Une cellule ne mène à une FICHE que si elle ne parle que
              // d'UN logement. À deux, on ne sait pas lequel viser — et deviner
              // enverrait sur le mauvais. Elle mène alors au jour, comme la
              // colonne.
              const versLogement = cliquable && ids.length === 1;
              const cible = versLogement
                ? { quoi: "logement", logement_id: ids[0], date: j.date }
                : { quoi: "jour", date: j.date };
              return (
                <g
                  key={j.date}
                  data-testid={`jours-cellule-${l.cle}`}
                  data-cible={cliquable ? cible.quoi : undefined}
                  role={cliquable ? "button" : undefined}
                  tabIndex={cliquable ? 0 : undefined}
                  aria-label={
                    cliquable
                      ? versLogement
                        ? `Ouvrir la fiche de ${nommer(ids[0])}`
                        : `Ouvrir la journée du ${libelleMoment(j.date)}`
                      : undefined
                  }
                  style={cliquable ? { cursor: "pointer" } : undefined}
                  onClick={cliquable ? () => onAller(cible) : undefined}
                  onKeyDown={
                    cliquable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onAller(cible);
                          }
                        }
                      : undefined
                  }
                >
                  <rect
                    x={x0}
                    y={y0 + 4}
                    width={w}
                    height={HAUTEUR_CELLULE - 8}
                    fill={l.couleur}
                    rx="3"
                  />
                  <text
                    x={x0 + w / 2}
                    y={y0 + HAUTEUR_CELLULE / 2 + 3.5}
                    textAnchor="middle"
                    fontSize={n > 7 ? "10.5" : "9.5"}
                    fontWeight="600"
                    fill={l.texte}
                  >
                    {libelle}
                  </text>
                  <title>{ids.map(nommer).join(", ")}</title>
                </g>
              );
            })}
          </g>
        );
      })}
      <line
        x1={MARGE.gauche}
        x2={LARGEUR - MARGE.droite}
        y1={H - MARGE.bas}
        y2={H - MARGE.bas}
        stroke={COULEUR.bord}
      />
    </Figure>
  );
}
